import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { FramedImage } from "../components/ui/FramedImage";
import { 
  ArrowRight, MapPin, Volume2, VolumeX, ChevronDown,
  Calendar, Award, Palette, Heart, Users, Globe
} from "lucide-react";
import { API } from "../apiConfig";


// Timeline data for Colin's journey
const TIMELINE_DATA = [
  {
    year: "Early Life",
    title: "Born to Country",
    description: "Born into the rich cultural heritage of the Gadigal and Bidjigal people, Colin's connection to art and storytelling began from his earliest days.",
    icon: Heart
  },
  {
    year: "1970s-80s",
    title: "Cultural Awakening",
    description: "Developed his distinctive wood-burning technique while deepening his understanding of traditional Aboriginal art forms and their spiritual significance.",
    icon: Palette
  },
  {
    year: "1990s",
    title: "Community Leadership",
    description: "Became a key figure at the Armajun Aboriginal Medical Centre, using art as a tool for healing and cultural preservation.",
    icon: Users
  },
  {
    year: "2000s",
    title: "Myall Creek Memorial",
    description: "Created the powerful 28-meter mural for the Myall Creek Massacre Memorial, honoring the Gomeroi people and their tragic 1838 massacre.",
    icon: Award
  },
  {
    year: "Present",
    title: "Elder & Ambassador",
    description: "Continues to create, teach, and inspire as a cultural Ambassador, including for the upcoming film 'Pemulwuy - the movie'.",
    icon: Globe
  }
];

// Extended biography sections
const BIOGRAPHY_SECTIONS = [
  {
    title: "The Artist",
    content: "Colin Isaacs' art is more than visual expression — it's a living connection to 65,000 years of continuous culture. His wood-burning masterpieces, known as pyrography, transform ordinary materials into sacred storytelling mediums. Each piece carries the weight of ancestral knowledge, ceremonial significance, and contemporary social commentary."
  },
  {
    title: "Cultural Heritage",
    content: "As an Elder of the Gadigal, Dharawal, Bidjigal, Eora, Goriwal, Dhunghutti, Noongar and Ngarabal clans, Colin carries the responsibility of preserving and transmitting cultural knowledge. His work serves as a bridge between ancient traditions and modern audiences, ensuring that the stories of his people continue to resonate across generations."
  },
  {
    title: "Artistic Philosophy",
    content: "\"Art is my way of speaking to the world,\" Colin explains. \"Every mark I make carries a story—stories of my ancestors, stories of our land, stories that need to be heard.\" His philosophy centers on the belief that art can heal wounds, bridge divides, and create understanding between cultures."
  },
  {
    title: "Legacy & Impact",
    content: "Beyond his artistic achievements, Colin's impact extends into community health, cultural preservation, and reconciliation efforts. His involvement with the Woolan-gang Gweagle Bidgigal tribal Governing Council demonstrates his commitment to Indigenous governance and self-determination."
  }
];

const ArtistPage = () => {
  const { artistId } = useParams();
  const [artist, setArtist] = useState(null);
  const [products, setProducts] = useState([]);
  const [cmsContent, setCmsContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [activeTimelineIndex, setActiveTimelineIndex] = useState(0);

  useEffect(() => {
    fetchData();
  }, [artistId]);

  // Auto-advance timeline
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTimelineIndex((prev) => (prev + 1) % TIMELINE_DATA.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      let artistData;
      
      if (artistId) {
        const response = await axios.get(`${API}/artists/${artistId}`);
        artistData = response.data;
      } else {
        const artistsRes = await axios.get(`${API}/artists`);
        if (artistsRes.data.length > 0) {
          artistData = artistsRes.data[0];
        }
      }
      
      try {
        const cmsRes = await axios.get(`${API}/cms/artist`);
        const cmsObj = {};
        cmsRes.data.forEach(item => {
          cmsObj[item.section] = item.content;
        });
        setCmsContent(cmsObj);
      } catch (e) {
        console.error("CMS fetch error:", e);
      }
      
      if (artistData) {
        setArtist(artistData);
        const productsRes = await axios.get(`${API}/products?artist_id=${artistData.artist_id}`);
        setProducts(productsRes.data);
      }
    } catch (error) {
      console.error("Error fetching artist:", error);
    } finally {
      setLoading(false);
    }
  };

  const cms = (key, artistKey, fallback) => {
    if (cmsContent[key]) return cmsContent[key];
    if (artistKey && artist && artist[artistKey]) return artist[artistKey];
    return fallback;
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto animate-pulse">
            <div className="h-96 bg-muted rounded-2xl mb-12" />
            <div className="h-12 bg-muted rounded w-1/3 mb-4" />
            <div className="h-6 bg-muted rounded w-1/4 mb-8" />
            <div className="h-32 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12 text-center">
          <h1 className="text-2xl font-medium mb-4">Artist not found</h1>
          <Link to="/products">
            <Button className="rounded-full">Browse Collection</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="artist-page">
      <Navigation />

      {/* Hero Section with Smoking Ceremony Video */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0">
          <video
            autoPlay
            loop
            muted={isMuted}
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ playbackRate: 0.25 }}
            ref={(el) => { if (el) el.playbackRate = 0.25; }}
            data-testid="artist-hero-video"
          >
            <source src="https://customer-assets.emergentagent.com/job_aboriginal-art/artifacts/qv9lj99o_2048da15-3756-4f62-bde3-0ef333b9247f.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Badge variant="outline" className="text-sm tracking-widest uppercase border-white/30 text-white bg-black/20 backdrop-blur-sm">
              Meet the Artist
            </Badge>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="text-6xl md:text-8xl font-light mb-6 text-white drop-shadow-lg"
            data-testid="artist-hero-title"
          >
            <em>Colin Isaacs</em>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto drop-shadow-md"
          >
            Gadigal and Bidjigal Elder, lifelong artist, cultural Ambassador — returning to Country after 45 years of service to community.
          </motion.p>
        </div>

        {/* Mute Toggle */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          onClick={() => setIsMuted(!isMuted)}
          className="absolute bottom-24 right-8 z-10 p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
          data-testid="mute-toggle-btn"
        >
          {isMuted ? (
            <VolumeX className="h-5 w-5 text-white" />
          ) : (
            <Volume2 className="h-5 w-5 text-white" />
          )}
        </motion.button>

        {/* Scroll Down Arrow */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          onClick={() => document.getElementById('artist-content').scrollIntoView({ behavior: 'smooth' })}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 cursor-pointer"
          data-testid="scroll-down-btn"
        >
          <span className="text-xs tracking-widest uppercase text-white/50">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <ChevronDown className="h-6 w-6 text-white/70" />
          </motion.div>
        </motion.button>
      </section>

      {/* Artist Detail Section */}
      <section id="artist-content" className="py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Artist Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl bg-card">
                <img
                  src={artist.image_url || "https://images.unsplash.com/photo-1525599428495-0441bd5c67de"}
                  alt={artist.name}
                  className="w-full h-full object-cover artwork-image"
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                  data-testid="artist-image"
                />
              </div>

              {/* Floating Quote Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="absolute -bottom-8 -right-4 md:right-8 max-w-xs bg-card/95 backdrop-blur-xl p-6 rounded-2xl shadow-xl border border-border/50"
              >
                <p className="font-accent text-lg italic text-muted-foreground">
                  "Art is my way of speaking to the world. Every mark carries a story."
                </p>
                <p className="text-sm text-primary mt-2 font-medium">— Colin Isaacs</p>
              </motion.div>
            </motion.div>

            {/* Artist Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-4xl md:text-6xl font-light mb-4" data-testid="artist-name">
                <em>{cms('name', 'name', artist.name)}</em>
              </h2>
              
              <p className="text-xl text-primary mb-4 font-medium">
                The White Wallaby
              </p>
              
              <p className="text-lg text-muted-foreground mb-6">
                Gadigal & Bidjigal Elder | Master Pyrography Artist | Cultural Ambassador
              </p>
              
              <div className="flex items-center gap-2 text-muted-foreground mb-8">
                <MapPin className="h-5 w-5 text-primary" />
                <span className="text-lg">{artist.region}</span>
              </div>

              <p className="drop-cap text-lg text-muted-foreground leading-loose mb-8">
                {artist.biography}
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/products" data-testid="artist-view-works-btn">
                  <Button 
                    size="lg" 
                    className="rounded-full px-8 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 btn-hover"
                  >
                    View His Works
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="rounded-full px-8 border-2"
                  onClick={() => document.getElementById('timeline')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  His Journey
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Interactive Timeline Section */}
      <section id="timeline" className="py-24 px-6 md:px-12 bg-card/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-widest uppercase text-primary mb-4">
              A Life of Art & Purpose
            </p>
            <h2 className="text-4xl md:text-5xl font-light">
              Colin's Journey
            </h2>
          </motion.div>

          {/* Timeline Navigation */}
          <div className="relative mb-12">
            {/* Progress Bar */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-muted -translate-y-1/2 rounded-full">
              <motion.div 
                className="h-full bg-primary rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: `${((activeTimelineIndex + 1) / TIMELINE_DATA.length) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>

            {/* Timeline Points */}
            <div className="relative flex justify-between">
              {TIMELINE_DATA.map((item, index) => {
                const Icon = item.icon;
                return (
                  <button
                    key={index}
                    onClick={() => setActiveTimelineIndex(index)}
                    className={`relative z-10 flex flex-col items-center gap-2 transition-all duration-300 ${
                      index <= activeTimelineIndex ? 'opacity-100' : 'opacity-50'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                      index === activeTimelineIndex 
                        ? 'bg-primary text-primary-foreground scale-110 shadow-lg shadow-primary/30' 
                        : index < activeTimelineIndex
                          ? 'bg-primary/80 text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                    }`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className={`text-xs font-medium hidden sm:block ${
                      index === activeTimelineIndex ? 'text-primary' : 'text-muted-foreground'
                    }`}>
                      {item.year}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Timeline Content */}
          <motion.div
            key={activeTimelineIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-card rounded-2xl p-8 md:p-12 shadow-lg border border-border/50"
          >
            <div className="flex items-start gap-6">
              <div className="hidden md:flex w-16 h-16 rounded-full bg-primary/10 items-center justify-center flex-shrink-0">
                {(() => {
                  const Icon = TIMELINE_DATA[activeTimelineIndex].icon;
                  return <Icon className="h-8 w-8 text-primary" />;
                })()}
              </div>
              <div>
                <p className="text-sm text-primary font-medium mb-2">
                  {TIMELINE_DATA[activeTimelineIndex].year}
                </p>
                <h3 className="text-2xl md:text-3xl font-medium mb-4">
                  {TIMELINE_DATA[activeTimelineIndex].title}
                </h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {TIMELINE_DATA[activeTimelineIndex].description}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Extended Biography Sections */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-widest uppercase text-primary mb-4">
              Understanding the Vision
            </p>
            <h2 className="text-4xl md:text-5xl font-light">
              The Story Behind the Art
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {BIOGRAPHY_SECTIONS.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-0 shadow-none bg-transparent border-t border-foreground/20 rounded-none">
                  <CardContent className="px-0 pt-8">
                    <p className="plate-no text-primary mb-4">{String(index + 1).padStart(2, "0")}</p>
                    <h3 className="text-2xl md:text-3xl font-light mb-4">
                      <em>{section.title}</em>
                    </h3>
                    <p className="text-muted-foreground leading-loose">
                      {section.content}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Story Section */}
      <section className="py-24 px-6 md:px-12 bg-card/50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-widest uppercase text-primary mb-4">
              In His Own Words
            </p>
            <h2 className="text-4xl md:text-5xl font-light mb-12">
              Connection to Country
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <blockquote className="text-2xl md:text-4xl font-accent italic text-foreground/80 leading-snug mb-12">
              "{artist.story}"
            </blockquote>
            
            {cms('cultural_background', 'cultural_significance', artist.cultural_significance) && (
              <div className="mt-12">
                <h3 className="text-2xl font-medium mb-6">Cultural Significance</h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {cms('cultural_background', 'cultural_significance', artist.cultural_significance)}
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      {artist.gallery_images && artist.gallery_images.length > 0 && (
        <section className="py-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm tracking-widest uppercase text-primary mb-4">
                Studio & Process
              </p>
              <h2 className="text-4xl md:text-5xl font-light mb-12">
                Creating Art
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {artist.gallery_images.map((img, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="aspect-[4/3] rounded-2xl overflow-hidden image-protection-wrapper"
                >
                  <img
                    src={img}
                    alt={`Gallery ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 artwork-image"
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Products Section */}
      {products.length > 0 && (
        <section className="py-24 px-6 md:px-12 bg-card/50" data-testid="artist-products">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-end mb-12">
              <div>
                <p className="text-sm tracking-widest uppercase text-primary mb-2">
                  Available Works
                </p>
                <h2 className="text-4xl md:text-5xl font-light">
                  The Collection
                </h2>
              </div>
              <Link to={`/products?artist_id=${artist.artist_id}`} className="text-muted-foreground hover:text-foreground flex items-center gap-2">
                View all
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.slice(0, 6).map((product, index) => (
                <motion.div
                  key={product.product_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/products/${product.product_id}`}
                    className="group block"
                    data-testid={`artist-product-${product.product_id}`}
                  >
                    <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-muted/30 mb-4">
                      <div className="absolute inset-0 flex items-center justify-center p-6 transition-transform duration-700 group-hover:scale-105">
                        <FramedImage
                          src={product.images[0] || "https://images.unsplash.com/photo-1663888672547-360e763e4ac7"}
                          alt={product.title}
                          frameScale={0.5}
                          imageScale={product.image_scale || 100}
                        />
                      </div>
                      {product.is_limited_edition && (
                        <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground z-10">
                          Limited Edition
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-xl font-light mb-1 group-hover:text-primary transition-colors">
                      <em>{product.title}</em>
                    </h3>
                    <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">
                      {product.medium || product.category?.replace("_", " ") || "Original Work"}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Call to Action */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-light mb-6">
              Own a Piece of Living History
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Each artwork by Colin Isaacs is a gateway to 65,000 years of continuous culture. 
              Become a custodian of this precious heritage.
            </p>
            <Link to="/products">
              <Button 
                size="lg" 
                className="rounded-full px-12 py-6 text-base bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 btn-hover"
              >
                Explore the Collection
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ArtistPage;
